import Image from 'next/image';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from 'next/link';

interface TheBaderDifferenceData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  book_button?: {
    label?: string;
    href?: string;
  };
  contact_button?: {
    label?: string;
    href?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title?: string;
          text?: string;
          step_number?: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",
  "subheading.text",
  "subheading.title",

  // text
  "text.text",

  // image
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",

  // buttons
  "book_button.label",
  "book_button.href",
  "contact_button.label",
  "contact_button.href",

  // content collection (steps)
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
] as const;

interface TheBaderDifferenceProps {
  data?: TheBaderDifferenceData;
}

export default function TheBaderDifference({ data }: TheBaderDifferenceProps) {
  // Image data
  const imageId = data?.image?.image_file?.image?.id || "";
  const imageFilename = data?.image?.image_file?.image?.filename_download || "";
  const imageAlt = data?.image?.alt || "";

  return (
    <section className="w-full py-8 xl:py-16 bg-neutral-100">
      {/* Hero Section */}
      <div className="relative w-full overflow-hidden">
          <div className="flex flex-col xl:flex-row xl:items-center xl:gap-16">
            {/* Image Container */}
            <div className="relative w-full xl:w-1/2 h-[30rem] sm:h-[29.5rem] xl:h-[40rem] overflow-hidden desktopFadeFromLeft">
              <Image
                src={imageId ? getReadableImageUrl(imageId, imageFilename) : ""}
                alt={imageAlt}
                fill
                className="object-cover xl:rounded-tr-[25rem] xl:rounded-br-[25rem]"
                sizes="(max-width: 1280px) 100vw, 50vw"
                quality={65}
              />
            </div>

            {/* Text Content */}
            <div className="px-8 xl:px-0 mt-8 xl:mt-0 xl:w-1/2 max-w-[37.5rem]">
              <div className="mb-2">
                <span className="text-xl font-medium font-greycliff uppercase tracking-tight text-primary">
                  {data?.subheading?.title}
                </span>
              </div>
              <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-8">
                {data?.heading?.title}
              </h2>
              <div
                className="text-[1rem] font-normal leading-normal lg:text-[1.125rem] font-greycliff text-primary
                  [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:pb-4
                  [&>ul>li]:mb-2 [&>ul>li:last-child]:mb-0
                  [&>ol]:list-decimal [&>ol]:pl-6
                  [&>ol>li]:mb-2 [&>ol>li:last-child]:mb-0
                  [&>p]:mb-4 [&>p:last-child]:mb-0
                  [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:[&>*]:text-[1.125rem]
                  [&_a]:underline [&_a]:transition-all [&_a:hover]:no-underline
                  [&>h3]:text-[1.5rem] [&>h3]:font-greycliff [&>h3]:font-medium [&>h3]:text-primary [&>h3]:leading-[2rem] [&>h3]:tracking-tight [&>h3]:mb-4"
                dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
              />
            </div>
          </div>
      </div>

      {/* Steps Section */}
      <div className="container mx-auto px-5 pt-8 xl:pt-16 xl:px-16">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-3 xl:gap-9">
          {data?.content_collection?.map((collection, index) => {
            // Find the title and text items separately - they're in different collection items
            const titleItem = collection?.site_content_collections_id?.collection.find(
              item => item.item.title
            )?.item;
            
            const textItem = collection?.site_content_collections_id?.collection.find(
              item => item.item.text
            )?.item;
            
            // Extract title and text values
            const title = titleItem?.title || "";
            const text = textItem?.text || "";
            
            return (
              <div key={index} className="flex gap-4 xl:gap-16">
                <span className="min-w-[2.5rem] xl:min-w-0 text-[4rem] xl:text-[6.5rem] font-normal font-greycliff leading-[5.5rem] xl:leading-[8.9rem] tracking-wider text-light">
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-[1.5rem] leading-9 font-essonnes font-light md:text-4xl md:leading-[3.06rem] lg:text-[2.5rem] lg:leading-[3.4rem] tracking-tight text-primary mb-4">
                    {title}
                  </h3>
                  <div
                    className="text-[1rem] font-normal leading-normal xl:text-[1.125rem] font-greycliff xl:leading-relaxed tracking-tight text-primary [&_a]:underline"
                    dangerouslySetInnerHTML={sanitizeHTML(text)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mt-12">
          <Link 
            href={data?.book_button?.href || "#"}
            className="w-full sm:w-auto px-12 py-3 bg-primary rounded-[3rem] text-white text-xl font-normal font-greycliff text-center"
          >
            {data?.book_button?.label}
          </Link>
          <Link href={data?.contact_button?.href || "#"} className="flex items-center gap-3">
            <span className="text-xl font-normal font-greycliff text-primary">
              {data?.contact_button?.label}
            </span>
            <svg
              className="w-4 h-4 rotate-45"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 16L16 1M16 1H1M16 1V16" stroke="#193762" strokeWidth="2"/>
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
} 